import api from './api';
import type { LoginCredentials, RegisterData, AuthResponse, User } from '@/types';

class AuthService {
  private tokenKey = 'teaching_app_token';
  private userKey = 'teaching_app_user';

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  getUser(): User | null {
    const userStr = localStorage.getItem(this.userKey);
    return userStr ? JSON.parse(userStr) : null;
  }

  private storeAuth(token: string, user: any): void {
    localStorage.setItem(this.tokenKey, token);
    localStorage.setItem(this.userKey, JSON.stringify(user));
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  clearAuth(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
    delete api.defaults.headers.common['Authorization'];
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  async initialize(): Promise<{ isAuthenticated: boolean; user: User | null; token: string | null }> {
    const token = this.getToken();
    if (!token) {
      return { isAuthenticated: false, user: null, token: null };
    }

    try {
      const user = await this.getProfile();
      return { isAuthenticated: true, user, token };
    } catch (error) {
      this.clearAuth();
      return { isAuthenticated: false, user: null, token: null };
    }
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/login', credentials);
    const { token, user } = response.data;

    this.storeAuth(token, user);
    return response.data;
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/register', data);
    const { token, user } = response.data;

    this.storeAuth(token, user);
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.warn('Backend logout failed:', error);
    } finally {
      this.clearAuth();
    }
  }

  async refreshToken(): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>('/auth/refresh');
      const { token, user } = response.data;

      this.storeAuth(token, user);
      return response.data;
    } catch (error: any) {
      this.clearAuth();
      throw error;
    }
  }

  async validateToken(): Promise<boolean> {
    try {
      const token = this.getToken();
      if (!token) {
        return false;
      }

      const response = await api.get('/auth/me');
      return response.status === 200;
    } catch (error) {
      this.clearAuth();
      return false;
    }
  }

  async getProfile(): Promise<User> {
    const response = await api.get('/auth/me');
    const user = response.data?.user || response.data;

    localStorage.setItem(this.userKey, JSON.stringify(user));
    return user;
  }

  async listUsers(): Promise<Array<{ id: number; email: string; name: string; role: string; created_at: string }>> {
    const response = await api.get('/auth/users');
    return response.data.users;
  }

  async removeStudent(userId: number): Promise<void> {
    await api.delete(`/auth/users/${userId}`);
  }

  async updateProfile(profileData: Partial<any>): Promise<any> {
    const response = await api.put('/auth/profile', profileData);
    const user = response.data;

    localStorage.setItem(this.userKey, JSON.stringify(user));
    return user;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.put('/auth/password', {
      currentPassword,
      newPassword
    });
  }
}

export const authService = new AuthService();
export default authService;
